import * as PIXI from 'pixi.js'
import { getGradientColor, adjustColorLuminance } from '../utils/colorUtils'
import { applyStemTexture } from '../utils/plantVisuals'
import { getPlantSegments } from '../utils/lSystem'

const alignToPixel = (value) => Math.round(value)
const alignPoint = (x, y) => ({
  x: alignToPixel(x),
  y: alignToPixel(y),
})

export function drawPlant(container, plant, config, isNewPixel = false, lastPixelsRef = null, plantId = null) {
  if (!container || !plant || !plant.config) return

  const plantConfig = plant.config
  const pixelSize = 6
  const segments = getPlantSegments(plant, plant.progress || 0, { pixelSize })

  let lastSegment = null
  segments.forEach((segment, index) => {
    lastSegment = drawSegmentPixel(
      container,
      segment,
      plantConfig,
      pixelSize,
      isNewPixel && index === segments.length - 1,
      lastPixelsRef,
      plantId,
    )
  })

  const totalClicks = plantConfig.clicksPerStage * plantConfig.stages
  if (plant.progress >= totalClicks && lastSegment) {
    drawPixelFlower(container, lastSegment.x2 * pixelSize, lastSegment.y2 * pixelSize, pixelSize, plantConfig)
  }
}

function drawSegmentPixel(container, segment, plantConfig, pixelSize, animate, lastPixelsRef, plantId) {
  const color = getGradientColor(plantConfig, segment.progressRatio)
  const thickness = Math.max(2, Math.round(pixelSize * segment.thicknessFactor))

  const start = alignPoint(segment.x1 * pixelSize, segment.y1 * pixelSize)
  const end = alignPoint(segment.x2 * pixelSize, segment.y2 * pixelSize)
  const dx = end.x - start.x
  const dy = end.y - start.y
  const length = Math.max(1, Math.hypot(dx, dy))
  const stepSize = Math.max(1, thickness * 0.8)
  const steps = Math.max(1, Math.ceil(length / stepSize))

  const pixel = new PIXI.Graphics()
  pixel.beginFill(color)
  for (let i = 0; i <= steps; i++) {
    const t = steps === 0 ? 0 : i / steps
    const px = dx * t
    const py = dy * t
    pixel.drawRect(px - thickness / 2, py - thickness / 2, thickness, thickness)
  }
  pixel.endFill()

  pixel.userData = pixel.userData || {}
  pixel.userData.branchKey = segment.branchKey

  const textureSeed = ((segment.x1 + segment.y1) * 0.23 + segment.depth * 0.17) % 1
  applyStemTexture(pixel, color, thickness, textureSeed)

  if (animate && lastPixelsRef && plantId) {
    pixel.position.set(start.x, start.y)
    lastPixelsRef.current[plantId] = {
      graphics: pixel,
      startX: start.x,
      startY: start.y,
      targetX: end.x,
      targetY: end.y,
      progress: 0,
    }
  } else {
    pixel.position.set(start.x, start.y)
  }

  container.addChild(pixel)
  return { x1: start.x / pixelSize, y1: start.y / pixelSize, x2: end.x / pixelSize, y2: end.y / pixelSize }
}

function drawPixelFlower(container, x, y, pixelSize, plantConfig) {
  const flowerColor = plantConfig?.flowerColor ? parseInt(plantConfig.flowerColor.replace('#', ''), 16) : 0xFFD700
  const center = new PIXI.Graphics()
  center.beginFill(flowerColor)
  center.drawRect(x - pixelSize, y - pixelSize, pixelSize * 2, pixelSize * 2)
  center.endFill()
  container.addChild(center)

  const petalPositions = [
    [-2, 0], [2, 0], [0, -2], [0, 2],
    [-1, -1], [1, -1], [-1, 1], [1, 1]
  ]

  const petalBase = adjustColorLuminance(flowerColor, 0.05)
  const petalShadow = adjustColorLuminance(flowerColor, -0.25)

  petalPositions.forEach(([dx, dy], index) => {
    const petal = new PIXI.Graphics()
    petal.beginFill(index % 2 === 0 ? petalBase : petalShadow)
    petal.drawRect(
      x + dx * pixelSize - pixelSize/2,
      y + dy * pixelSize - pixelSize/2,
      pixelSize,
      pixelSize
    )
    petal.endFill()
    container.addChild(petal)
  })

  const highlightColor = adjustColorLuminance(flowerColor, 0.35)
  const highlight = new PIXI.Graphics()
  highlight.beginFill(highlightColor, 0.6)
  highlight.drawCircle(x - pixelSize * 0.3, y - pixelSize * 0.6, pixelSize * 0.5)
  highlight.endFill()
  container.addChild(highlight)

  const shadowColor = adjustColorLuminance(flowerColor, -0.3)
  const shadow = new PIXI.Graphics()
  shadow.beginFill(shadowColor, 0.3)
  shadow.drawEllipse(x + pixelSize * 0.2, y + pixelSize * 0.8, pixelSize * 1.2, pixelSize * 0.5)
  shadow.endFill()
  container.addChild(shadow)
}
